/**
 * Coercion helpers for URL search parameters.
 *
 * These were written for the employees route and are now shared with the
 * Action Items worklist, which puts its filters in the URL the same way. They
 * live here rather than in a route module so importing them does not drag a
 * page component (and its whole render tree) along with them.
 *
 * @module lib/searchParams
 */

/** The only page-size values the UI exposes in the rows-per-page control. */
export const VALID_PAGE_SIZES = [10, 20, 50] as const;
export type ValidPageSize = (typeof VALID_PAGE_SIZES)[number];

/**
 * Safely coerces an unknown URL search value to a plain string.
 *
 * `String()` alone is insufficient because:
 * - A repeated param (e.g. `?status=active&status=terminated`) arrives as an
 *   array and `String(["active", "terminated"])` yields `"active,terminated"`.
 * - An unexpected object yields `"[object Object]"`.
 *
 * Strategy:
 * - String | number | boolean → `String(value)`
 * - Array → take the first element if it is a scalar, otherwise ""
 * - Anything else (object, null, undefined) → ""
 */
export function toSearchString(value: unknown): string {
  if (Array.isArray(value)) {
    const first = value[0];
    return typeof first === "string" || typeof first === "number" || typeof first === "boolean"
      ? String(first)
      : "";
  }
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return "";
}

/**
 * Safely coerces an unknown URL search value to a positive integer.
 * Returns `defaultValue` when the value is missing, non-numeric (including
 * partial numerics like "3abc"), or ≤ 0.
 *
 * Uses a whole-string digit check (`/^\d+$/`) before parsing so that
 * partial strings such as "3abc" do not silently parse as 3.
 */
export function toSearchInt(value: unknown, defaultValue: number): number {
  const str = toSearchString(value);

  if (!/^\d+$/.test(str)) {
    return defaultValue;
  }

  const parsed = Number(str);

  return parsed > 0 ? parsed : defaultValue;
}

/**
 * Clamps an unknown URL search value to a member of a finite allowlist.
 * Returns "" when the coerced string is not in the allowed set, so invalid
 * deep-linked values (e.g. `?status=foo`) map back to "All" rather than
 * leaving the corresponding <select> in an unmatched blank state or forwarding
 * garbage to the API.
 *
 * The generic parameter `T` lets callers infer a precise return type so the
 * narrowed value can be assigned to typed fields without extra casts at
 * call sites. Pass `as const` on the allowlist to enable literal inference:
 *
 * @example
 *   toSearchEnum(search.status, ["active", "terminated"] as const)
 *   // => "active" | "terminated" | ""
 */
export function toSearchEnum<T extends string>(value: unknown, allowedValues: readonly T[]): T | "" {
  const str = toSearchString(value);

  return (allowedValues as readonly string[]).includes(str) ? (str as T) : "";
}

/**
 * Coerces an unknown URL search value to one of the supported page-size
 * options (10, 20, 50). Values outside the allowed set default to 10 so a
 * deep-linked URL like `?pageSize=999` cannot request an unbounded page from
 * the API or leave the <select> in an unmatched state.
 */
export function toSearchPageSize(value: unknown): ValidPageSize {
  const parsed = toSearchInt(value, 10);

  return (VALID_PAGE_SIZES as readonly number[]).includes(parsed)
    ? (parsed as ValidPageSize)
    : 10;
}
